import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const QualityControlPanel = ({ queue, loading }) => {
  const [selectedJob, setSelectedJob] = useState(null);

  const recentJobs = queue?.slice(0, 10) || [];
  const qualityMetrics = {
    totalInspected: queue?.length || 0,
    passed: queue?.filter(j => j?.status === 'completed')?.length || 0,
    failed: queue?.filter(j => j?.status === 'failed')?.length || 0,
    passRate: queue?.length > 0 
      ? ((queue?.filter(j => j?.status === 'completed')?.length / queue?.length) * 100)?.toFixed(1)
      : 0
  };

  if (loading) {
    return <div className="text-center py-12 text-gray-500">Loading quality control data...</div>;
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900">Quality Control & Validation</h2>
        <p className="text-sm text-gray-600 mt-1">
          Monitor print quality and detect defects automatically
        </p>
      </div>
      {/* Quality Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-blue-900">Total Inspected</span>
            <Icon name="Eye" className="w-5 h-5 text-blue-600" />
          </div>
          <p className="text-2xl font-bold text-blue-900">{qualityMetrics?.totalInspected}</p>
        </div>

        <div className="bg-green-50 rounded-lg p-4 border border-green-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-green-900">Passed</span>
            <Icon name="CheckCircle" className="w-5 h-5 text-green-600" />
          </div>
          <p className="text-2xl font-bold text-green-900">{qualityMetrics?.passed}</p>
        </div>

        <div className="bg-red-50 rounded-lg p-4 border border-red-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-red-900">Failed</span>
            <Icon name="XCircle" className="w-5 h-5 text-red-600" />
          </div>
          <p className="text-2xl font-bold text-red-900">{qualityMetrics?.failed}</p>
        </div>

        <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-purple-900">Pass Rate</span>
            <Icon name="TrendingUp" className="w-5 h-5 text-purple-600" />
          </div>
          <p className="text-2xl font-bold text-purple-900">{qualityMetrics?.passRate}%</p>
        </div>
      </div>
      {/* Quality Checks */}
      <div className="bg-gray-50 rounded-lg p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Automated Quality Checks</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-start gap-3">
            <Icon name="CheckCircle" className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-gray-900">Color Calibration</h4>
              <p className="text-sm text-gray-600">Automatic color accuracy verification</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Icon name="CheckCircle" className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-gray-900">Print Alignment</h4>
              <p className="text-sm text-gray-600">Edge detection and positioning check</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Icon name="CheckCircle" className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-gray-900">Barcode Validation</h4>
              <p className="text-sm text-gray-600">QR/Barcode readability verification</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Icon name="CheckCircle" className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-gray-900">Defect Detection</h4>
              <p className="text-sm text-gray-600">Automatic identification of print defects</p>
            </div>
          </div>
        </div>
      </div>
      {/* Recent Inspections */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Inspections</h3>
        {recentJobs?.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Icon name="Inbox" className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p>No inspections available</p>
          </div>
        ) : (
          <div className="space-y-3">
            {recentJobs?.map((job) => (
              <div 
                key={job?.id}
                className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => setSelectedJob(job)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Icon 
                      name={job?.status === 'completed' ? 'CheckCircle' : 'XCircle'} 
                      className={`w-5 h-5 ${
                        job?.status === 'completed' ? 'text-green-600' : 'text-red-600'
                      }`}
                    />
                    <div>
                      <p className="font-medium text-gray-900">{job?.employeeName}</p>
                      <p className="text-sm text-gray-600">{job?.department}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900">
                      {job?.status === 'completed' ? 'Passed' : 'Failed'}
                    </p>
                    <p className="text-xs text-gray-500">
                      {new Date(job.printedAt)?.toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default QualityControlPanel;
